import {MutableRefObject, useCallback} from 'react';
import {Elements, FlowElement, useStoreActions, useZoomPanHelper} from 'react-flow-renderer';
import {PlanElementData} from '../../components/plan-element-view';
import {resolveRoomAtPoint} from '../../utilities/resolve-room-at-point.utility';

export interface UseRoomPolygonSelectionResult {
  onElementClick: (event: React.MouseEvent, element: FlowElement) => void;
  onPaneClick: () => void;
}

/**
 * Replaces React Flow AABB room selection with polygon (smallest-containing) hit-test.
 * Must be used under ReactFlowProvider.
 */
export const useRoomPolygonSelection = (
  roomElements: Elements<PlanElementData>,
  reactFlowWrapper: MutableRefObject<HTMLDivElement | null>,
  roomsSelectable: boolean,
): UseRoomPolygonSelectionResult => {
  const {project} = useZoomPanHelper();
  const setSelectedElements = useStoreActions((actions) => actions.setSelectedElements);

  const projectEventToFloor = useCallback(
    (event: React.MouseEvent) => {
      if (!reactFlowWrapper.current) {
        return null;
      }
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      return project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });
    },
    [project, reactFlowWrapper],
  );

  const selectRoomById = useCallback(
    (roomId: string | null) => {
      if (!roomId) {
        setSelectedElements([]);
        return;
      }

      const roomElement = roomElements.find(
        (element) => element.data?.id === roomId || element.id === `rooms-${roomId}`,
      );

      if (!roomElement) {
        setSelectedElements([]);
        return;
      }

      setSelectedElements([roomElement]);
    },
    [roomElements, setSelectedElements],
  );

  const onElementClick = useCallback(
    (event: React.MouseEvent, element: FlowElement) => {
      if (element.type !== 'rooms') {
        return;
      }

      if (!roomsSelectable) {
        setSelectedElements([]);
        return;
      }

      const point = projectEventToFloor(event);
      if (!point) {
        return;
      }

      const clickedRoomId = (element.data as PlanElementData | undefined)?.id ?? element.id?.replace(/^rooms-/, '');
      const matchedRoom = resolveRoomAtPoint(point, roomElements, clickedRoomId);
      selectRoomById(matchedRoom?.id ?? null);
    },
    [projectEventToFloor, roomElements, roomsSelectable, selectRoomById, setSelectedElements],
  );

  const onPaneClick = useCallback(() => {
    setSelectedElements([]);
  }, [setSelectedElements]);

  return {
    onElementClick,
    onPaneClick,
  };
};
